import {
  bifrostImageToDataUrl,
  native,
  type BifrostImage,
} from '@forest/native-services'
import type { CataloguePrice } from '~/lib/catalogue-price'
import { logNonFatal } from '~/lib/diagnostics'
import { preloadImage } from '~/lib/preload-image'
import type { ShopProduct } from './bootstrap'

// The catalogue shared by every card grid — Classic, Daily Picks, and the hero
// row: the product and candidate shapes, and the two hydration stages of
// ADR 0020 (grid-stage art and title per candidate; tree detail assets on
// demand). Each collection derives its own candidates in its own module
// (classic-catalogue, daily-picks, main-products) and feeds them through
// hydrateGridCandidate.

export interface CatalogueImage {
  src: string
}

// The two content kinds a catalogue card can carry. Catalogue identity in
// the featured collections is (content type, gid): tree and land-skin gids
// are separate namespaces (GLOSSARY.md "Owned land skin").
export type CatalogueContent = 'land_skin' | 'tree_type'

// Catalogue identity in every card grid: (content type, gid). A tree and a
// land skin may share a gid number and are still two products (GLOSSARY.md
// "Owned land skin", "Classic product visibility").
export interface CatalogueIdentity {
  content: CatalogueContent
  gid: number
}

// One page of the land-skin detail's image pager (GLOSSARY.md "Detail
// slide"): a remote image and the title suffix appended to the product title
// while it shows — resolved copy, '' for none.
export interface DetailSlide {
  imageUrl: string
  titleSuffix: string
}

// The pager always has a first slide: the fallback to the icon is decided at
// derivation, so "a land skin always has something to show" is a fact of the
// type rather than a check at every reader.
export type DetailSlides = [DetailSlide, ...DetailSlide[]]

// A land skin's remote art (GLOSSARY.md "Land-skin art"): the item type's
// top-level icon_image_url for the catalogue card and unlock-success
// surface, and the detail dialog's slides — display metadata's optional
// `details` list, or a lone slide of the icon when the list yields nothing.
// Defined here, beside the content kinds, so land-skin-art.ts depends on
// this module and not the other way round.
export interface LandSkinArt {
  detailSlides: DetailSlides
  iconUrl: string
}

// What a candidate's content kind brings with it: a tree needs nothing beyond
// its kind (its art is a native lookup at hydration), a land skin carries its
// remote art, known at derivation (ADR 0013). Discriminating on content keeps
// "a land skin always has art" a fact of the type, not of a comment.
export type CandidateContent =
  | { content: 'tree_type' }
  | { content: 'land_skin'; landSkinArt: LandSkinArt }

interface CatalogueProductBase {
  gid: number
  id: number
  owned: boolean
  price: CataloguePrice
  productImage: CatalogueImage
  title: string
}

// A tree's phase count (GLOSSARY.md "Tree species art"), learned with its
// card so the detail opens already shaped for its minute-range preview or
// its absence (ADR 0050). null: the phase 5 lookup failed, so the detail
// learns the count from its own assets.
export type TreePhaseCount = 4 | 7 | null

export type TreeProduct = CatalogueProductBase & {
  content: 'tree_type'
  phaseCount: TreePhaseCount
}

export type LandSkinProduct = CatalogueProductBase & {
  content: 'land_skin'
  // The land-skin detail's slides, whose images load when the dialog opens —
  // unlike productImage, which is paintable the moment the card exists. The
  // fallback chain always yields at least one (GLOSSARY.md "Land-skin art").
  detailSlides: DetailSlides
}

// Grid-stage product: everything the catalogue grid and unlock flow render.
// Tree detail-only assets live in TreeDetailAssets and hydrate on demand
// (ADR 0020); a land-skin detail needs nothing beyond detailSlides.
export type CatalogueProduct = LandSkinProduct | TreeProduct

export interface TreeDetailAssets {
  description: string
  // null marks a four-phase tree: the detail renders no minute-range strip.
  phaseImages:
    | [CatalogueImage, CatalogueImage, CatalogueImage, CatalogueImage]
    | null
}

// A candidate before its price is known — what a collection's derivation
// validates before narrowing it into a candidate.
export type CatalogueCandidateDraft = CandidateContent & {
  gid: number
  owned: boolean
  product: ShopProduct
}

export type CatalogueCandidate = CatalogueCandidateDraft & {
  // Derived from purchase_type once validation passed: only coin-priced,
  // gem-priced, and free products survive derivation.
  price: CataloguePrice
}

// Applied per native call, not per tree: the bridge rejects outbound messages
// past its shared transport queue limit (OUTBOUND_QUEUE_FULL at 100 pending
// sends). One module-level limiter spans both hydration stages so the bound
// still holds when a detail opens while the grid stage is running.
const HYDRATION_CONCURRENCY = 50

type NativeCallLimiter = <T>(task: () => Promise<T>) => Promise<T>

function createNativeCallLimiter(limit: number): NativeCallLimiter {
  let active = 0
  const waiting: (() => void)[] = []
  return async (task) => {
    if (active < limit) active++
    else await new Promise<void>((resolve) => waiting.push(resolve))
    try {
      return await task()
    } finally {
      // Hand the slot to the next waiter directly so a burst of new callers
      // cannot slip past the cap between a release and a waiter waking up.
      const next = waiting.shift()
      if (next) next()
      else active--
    }
  }
}

const limitNativeCall = createNativeCallLimiter(HYDRATION_CONCURRENCY)

// The grid stage's two ways of losing a product: its art call failed, or
// answered with nothing usable. Derivation-time reasons belong to each
// collection's own module.
type HydrationOmissionReason =
  | 'asset_hydration_failed'
  | 'missing_required_asset'

// Daily-pick and main-product cards follow the Classic card's hydration
// rules but report their omissions under their own collection's event.
export type ProductOmissionEvent =
  | 'shop_classic_product_omitted'
  | 'shop_daily_pick_product_omitted'
  | 'shop_main_product_omitted'

type DetailFailureReason =
  | 'asset_hydration_failed'
  | 'description_hydration_failed'
  | 'missing_required_asset'
  | 'partial_late_phase_assets'

function reportOmission(
  reason: HydrationOmissionReason,
  productIds: number[],
  gid: number,
  event: ProductOmissionEvent,
): void {
  logNonFatal(event, {
    reason,
    product_ids: productIds.join(','),
    gid: String(gid),
  })
}

// Detail assets are no longer omission material: the tree already sits in the
// grid, so a failure here becomes the thrown error the detail query retries.
function reportDetailFailure(reason: DetailFailureReason, gid: number): Error {
  logNonFatal('shop_classic_detail_assets_failed', {
    reason,
    gid: String(gid),
  })
  return new Error(reason)
}

function toCatalogueImage(image: BifrostImage): CatalogueImage | null {
  if (
    typeof image.base64 !== 'string' ||
    image.base64.trim().length === 0 ||
    typeof image.mimeType !== 'string' ||
    !image.mimeType.startsWith('image/')
  ) {
    return null
  }

  return { src: bifrostImageToDataUrl(image) }
}

// null means native answered but had no usable image; a bridge failure
// propagates as a rejection for the caller to attribute.
function imageFromResult(
  result: { success: true; data: BifrostImage } | { success: false },
  logContext: Record<string, unknown>,
): CatalogueImage | null {
  if (!result.success) return null
  const image = toCatalogueImage(result.data)
  if (!image) {
    console.error('[shop] asset payload invalid', logContext)
  }
  return image
}

async function fetchImage(name: string): Promise<CatalogueImage | null> {
  const result = await limitNativeCall(() => native.assets.getImageByName(name))
  return imageFromResult(result, { name })
}

// A tree's species art: the native asset its catalogue card shows.
const treeArtName = (gid: number) => `tree_type_${gid}_phase_product`

const treePhaseName = (gid: number, phase: number) =>
  `tree_type_${gid}_phase_${phase}`

// Native has no asset-existence call, so the phase 5 lookup itself decides
// (ADR 0050): an asset found means seven phases — an unusable payload is the
// detail's to reject as malformed — and none found means four. A failed call
// leaves the count unknown rather than losing the card over a lookup that
// only shapes its detail.
async function learnPhaseCount(gid: number): Promise<TreePhaseCount> {
  try {
    const result = await limitNativeCall(() =>
      native.assets.getImageByName(treePhaseName(gid, 5)),
    )
    return result.success ? 7 : 4
  } catch (error) {
    console.error('[shop] tree phase count lookup failed', {
      gid,
      reason: error,
    })
    return null
  }
}

// A tree's species art for a surface outside its own card (the starter
// pack's Reward reveal). null = native has no usable image; a bridge failure
// rejects.
export function fetchTreeArt(gid: number): Promise<CatalogueImage | null> {
  return fetchImage(treeArtName(gid))
}

// Land-skin card art is a remote URL (GLOSSARY.md "Land-skin art"), preloaded
// so the slot holds its skeleton until the image is paintable — the same
// contract native art gives a tree card. Not under the native-call limiter:
// that bound exists for the bridge's outbound queue, not the network.
async function preloadLandSkinImage(url: string): Promise<CatalogueImage> {
  await preloadImage(url)
  return { src: url }
}

// A native string catalog answer as copy: the value, or '' for a miss — an
// empty answer or the key echoed back (GLOSSARY.md "Echo-key miss").
function readLocalizedValue(value: unknown, key: string): string {
  return typeof value === 'string' &&
    value.trim() !== '' &&
    value.trim() !== key
    ? value
    : ''
}

async function localize(
  key: string,
  logContext: Record<string, unknown>,
): Promise<string> {
  try {
    const value = await limitNativeCall(() =>
      native.l10n.getLocalizedString({ key }),
    )
    return readLocalizedValue(value, key)
  } catch (error) {
    console.error('[shop] catalogue localization failed', {
      key,
      reason: error,
      ...logContext,
    })
    return ''
  }
}

// A tree or land skin's title key in the native string catalog. Land skins
// spell it `landskin`, unlike the `land_skin` content type (GLOSSARY.md
// "Content title key" spelling trap).
function contentTitleKey(content: CatalogueContent, gid: number): string {
  return content === 'tree_type'
    ? `tree_type_${gid}_title`
    : `landskin_${gid}_title`
}

// A tree or land skin's native-catalog title, '' on a miss or a failed
// lookup — for surfaces that name a content outside its own card (the
// starter pack's captions).
export function localizeContentTitle(
  content: CatalogueContent,
  gid: number,
  logContext: Record<string, unknown>,
): Promise<string> {
  return localize(contentTitleKey(content, gid), { gid, ...logContext })
}

export async function hydrateGridCandidate(
  candidate: CatalogueCandidate,
  omissionEvent: ProductOmissionEvent = 'shop_classic_product_omitted',
): Promise<CatalogueProduct | null> {
  const { gid, owned, price, product } = candidate
  // Everything content-specific in one place. Tree art is a native asset
  // fetched by name; land-skin art is the remote icon URL derivation already
  // validated, so its artLabel names that URL in diagnostics.
  const lookups =
    candidate.content === 'tree_type'
      ? {
          artLabel: treeArtName(gid),
          fetchArt: () => fetchTreeArt(gid),
          learnPhaseCount: () => learnPhaseCount(gid),
        }
      : {
          artLabel: candidate.landSkinArt.iconUrl,
          fetchArt: () => preloadLandSkinImage(candidate.landSkinArt.iconUrl),
          learnPhaseCount: async () => undefined,
        }

  const [title, productImageResult, phaseCount] = await Promise.all([
    localize(contentTitleKey(candidate.content, gid), {
      gid,
      product_id: product.id,
    }),
    lookups.fetchArt().then(
      (image) => ({ image }),
      (reason: unknown) => ({ reason }),
    ),
    lookups.learnPhaseCount(),
  ])

  if ('reason' in productImageResult) {
    console.error('[shop] catalogue asset fetch failed', {
      gid,
      name: lookups.artLabel,
      product_id: product.id,
      reason: productImageResult.reason,
    })
    reportOmission('asset_hydration_failed', [product.id], gid, omissionEvent)
    return null
  }
  if (!productImageResult.image) {
    console.error('[shop] catalogue required asset missing', {
      gid,
      missing: lookups.artLabel,
      product_id: product.id,
    })
    reportOmission('missing_required_asset', [product.id], gid, omissionEvent)
    return null
  }

  const base = {
    gid,
    id: product.id,
    owned,
    price,
    productImage: productImageResult.image,
    title,
  }
  return candidate.content === 'tree_type'
    ? { ...base, content: 'tree_type', phaseCount: phaseCount ?? null }
    : {
        ...base,
        content: 'land_skin',
        detailSlides: candidate.landSkinArt.detailSlides,
      }
}

// A known four-phase tree shows no phase asset, so it loads its description
// alone and skips the asset-set validation (ADR 0050); a seven-phase or
// unknown tree loads and validates phases 4–7.
export async function hydrateTreeDetailAssets(
  gid: number,
  phaseCount: TreePhaseCount = null,
): Promise<TreeDetailAssets> {
  const descriptionKey = `tree_type_${gid}_description`
  const phaseNames = [4, 5, 6, 7].map((phase) => treePhaseName(gid, phase))

  // A localization miss (key echoed back, empty value) is absent content and
  // stays an empty string, but a failed bridge call must fail the query so
  // the detail dialog offers retry instead of degrading (ADR 0020).
  const describeTree = async () => {
    try {
      const value = await limitNativeCall(() =>
        native.l10n.getLocalizedString({ key: descriptionKey }),
      )
      return readLocalizedValue(value, descriptionKey)
    } catch (error) {
      console.error('[shop] catalogue localization failed', {
        gid,
        key: descriptionKey,
        reason: error,
      })
      throw reportDetailFailure('description_hydration_failed', gid)
    }
  }

  if (phaseCount === 4) {
    return { description: await describeTree(), phaseImages: null }
  }

  const [description, phaseResults] = await Promise.all([
    describeTree(),
    Promise.allSettled(phaseNames.map((name) => fetchImage(name))),
  ])

  const rejections = phaseResults.flatMap((result, index) =>
    result.status === 'rejected'
      ? [{ name: phaseNames[index]!, reason: result.reason }]
      : [],
  )
  if (rejections.length > 0) {
    for (const { name, reason } of rejections) {
      console.error('[shop] catalogue asset fetch failed', {
        gid,
        name,
        reason,
      })
    }
    throw reportDetailFailure('asset_hydration_failed', gid)
  }

  const [phase4, phase5, phase6, phase7] = phaseResults.map((result) =>
    result.status === 'fulfilled' ? result.value : null,
  )

  if (!phase4) {
    console.error('[shop] catalogue required asset missing', {
      gid,
      missing: phaseNames[0]!,
    })
    throw reportDetailFailure('missing_required_asset', gid)
  }

  const latePhases = [phase5, phase6, phase7]
  const latePhaseCount = latePhases.filter(Boolean).length
  if (latePhaseCount !== 0 && latePhaseCount !== 3) {
    console.error('[shop] tree detail late-phase assets partial', {
      gid,
      missing: latePhases
        .flatMap((phase, index) => (phase ? [] : [phaseNames[index + 1]!]))
        .join(','),
    })
    throw reportDetailFailure('partial_late_phase_assets', gid)
  }

  return {
    description,
    phaseImages:
      latePhaseCount === 3 ? [phase4, phase5!, phase6!, phase7!] : null,
  }
}

// Detail-stage assets hydrate on demand per tree. Failures surface as the
// query's error state with a manual retry in the detail dialog, so automatic
// retries stay off. The lookups are bridge calls, not network requests, so
// the query never pauses on navigator.onLine — the launch deep link's reveal
// waits on this query and must not stall offline. Keyed by gid alone: the
// launch deep link prefetches before its slot learns the phase count, and
// the dialog must pick that prefetch up.
export const treeDetailAssetsQuery = (
  gid: number,
  phaseCount: TreePhaseCount = null,
) => ({
  queryKey: ['shop', 'tree-detail-assets', gid] as const,
  queryFn: () => hydrateTreeDetailAssets(gid, phaseCount),
  gcTime: Infinity,
  networkMode: 'always' as const,
  retry: false,
  staleTime: Infinity,
})

export interface LandSkinDetailAssets {
  description: string
}

// The land-skin detail's stage of ADR 0020: the localized description alone —
// a land skin has no phase strip. Title keys spell land skins `landskin`
// (GLOSSARY.md "Content title key"), and so does the description's. A miss is
// empty copy; a failed bridge call fails the query so the dialog offers retry.
export async function hydrateLandSkinDetailAssets(
  gid: number,
): Promise<LandSkinDetailAssets> {
  const descriptionKey = `landskin_${gid}_description`
  try {
    const value = await limitNativeCall(() =>
      native.l10n.getLocalizedString({ key: descriptionKey }),
    )
    return { description: readLocalizedValue(value, descriptionKey) }
  } catch (error) {
    console.error('[shop] catalogue localization failed', {
      gid,
      key: descriptionKey,
      reason: error,
    })
    logNonFatal('shop_land_skin_detail_assets_failed', {
      reason: 'description_hydration_failed',
      gid: String(gid),
    })
    throw new Error('description_hydration_failed')
  }
}

export const landSkinDetailAssetsQuery = (gid: number) => ({
  queryKey: ['shop', 'land-skin-detail-assets', gid] as const,
  queryFn: () => hydrateLandSkinDetailAssets(gid),
  gcTime: Infinity,
  retry: false,
  staleTime: Infinity,
})
